import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

export const Route = createFileRoute('/squash-league-software')({
    component: RouteComponent,
})

const FEATURES = [
    {code: "BX", title: "Box leagues", desc: "Split members into boxes of four to six, then promote and relegate after each round."},
    {code: "LD", title: "Ladder challenges", desc: "Let members challenge players above them and climb the club ladder."},
    {code: "GM", title: "Best-of-five games", desc: "Record squash results game by game, the way the match was actually played."},
    {code: "CT", title: "Court slots", desc: "Give every fixture a court and time so members know when they are on."},
    {code: "DV", title: "Member divisions", desc: "Keep beginners, improvers and first-team players in their own groups."},
    {code: "RK", title: "Club rankings", desc: "Publish standings and rankings that update as results come in."},
];

const LEAGUE_TYPES = [
    {name: "Box Leagues", sub: "Small groups with promotion and relegation", to: "/round-robin-tournament-generator/"},
    {name: "Ladder Leagues", sub: "Challenges and ranking movement", to: "/ladder-league-generator/"},
    {name: "Squash Tournaments", sub: "Draws, seeds and knockout rounds", to: "/squash-tournament-software/"},
    {name: "Sports League Management", sub: "Recurring fixtures and standings", to: "/sports-league-management-software/"},
];

const BENEFITS = [
    {title: "Members arrange their own matches", desc: "Everyone can see who they still owe a match and sort a time between them."},
    {title: "Rounds roll over cleanly", desc: "Finish a box round, promote and relegate, and start the next one."},
    {title: "Standings members trust", desc: "Results feed the table directly, so nobody chases the club secretary."},
];

const USE_CASES = [
    {title: "For Squash Clubs", desc: "Run member box leagues and ladders through the whole season."},
    {title: "For Club Secretaries", desc: "Stop rebuilding boxes and tables by hand every few weeks."},
    {title: "For Coaches", desc: "Give squads competitive matches between coaching sessions."},
    {title: "For Racketball & Squash 57", desc: "Run parallel leagues for other formats played on the same courts."},
    {title: "For Multi-Court Clubs", desc: "Spread fixtures across courts and peak times without clashes."},
    {title: "For Internal Championships", desc: "Feed league results into an end-of-season club championship."},
];

const COMPARE = [
    {need: "Box rounds", manual: "Rebuilt by hand every few weeks", scorepal: "Boxes generated with promotion and relegation"},
    {need: "Ladder movement", manual: "Manual edits after each challenge", scorepal: "Rankings move automatically with each result"},
    {need: "Game scores", manual: "Only the winner gets written down", scorepal: "Every game recorded, best of five"},
    {need: "Court allocation", manual: "Clubhouse noticeboard", scorepal: "Each match carries its court and time"},
    {need: "Member experience", manual: "Members ask who they play next", scorepal: "Fixtures and standings on a public page"},
];

const FAQS = [
    {
        q: 'What is squash league software?',
        a: <>Squash league software helps clubs run recurring member competitions — box leagues and ladders — by organising fixtures, collecting game scores, publishing standings and tracking rankings across a season.</>,
    },
    {
        q: 'Can ScorePal run squash box leagues?',
        a: <>Yes. Members can be split into boxes where everyone plays everyone, with results feeding a table you use to promote and relegate players between rounds.</>,
    },
    {
        q: 'Does ScorePal support ladder leagues?',
        a: <>Yes. ScorePal supports <Link to={"/ladder-league-generator/" as any} className="text-[#082F8A] underline font-semibold">ladder-style</Link> squash leagues where players challenge others above them and move up or down the rankings.</>,
    },
    {
        q: 'Can results be recorded game by game?',
        a: <>Yes. A best-of-five squash match is stored game by game, so an 11-9 8-11 11-7 11-5 scoreline is kept in full rather than reduced to a single number.</>,
    },
    {
        q: 'Is this different from squash tournament software?',
        a: <>Yes. Squash league software is built for recurring club play across a season. <Link to={"/squash-tournament-software/" as any} className="text-[#082F8A] underline font-semibold">Squash tournament software</Link> is built for one-off events with draws, seeds and knockout rounds.</>,
    },
];

function RouteComponent() {
    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Squash League Software for Clubs & Organisers | ScorePal</title>
            <meta name="description" content="Run squash leagues with ScorePal. Manage box leagues, ladders, court slots, game-by-game scores, club rankings and season standings from one platform." />
            <link rel="canonical" href={`${config.publicUrl}/squash-league-software/`} />
            <meta property="og:title" content="Squash League Software | ScorePal" />
            <meta property="og:description" content="Create squash box leagues and ladders, record game scores and publish standings with ScorePal." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/squash-league-software/`} />
            <meta property="og:image" content="https://scorepal.co/images/squash-league-software-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Squash League Software | ScorePal" />
            <meta name="twitter:description" content="Run squash box leagues, ladders, rankings and standings from one platform." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "ScorePal Squash League Software",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "Squash league software for managing box leagues, ladders, court slots, game scores, standings and club rankings.",
                "url": "https://scorepal.co/squash-league-software/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is squash league software?", "acceptedAnswer": {"@type": "Answer", "text": "Squash league software helps clubs run recurring member competitions such as box leagues and ladders, organising fixtures, collecting game scores, publishing standings and tracking rankings across a season."}},
                    {"@type": "Question", "name": "Can ScorePal run squash box leagues?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Members can be split into boxes where everyone plays everyone, with results feeding a table you use to promote and relegate players between rounds."}},
                    {"@type": "Question", "name": "Does ScorePal support ladder leagues?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal supports ladder-style squash leagues where players challenge others above them and move up or down the rankings."}},
                    {"@type": "Question", "name": "Can results be recorded game by game?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. A best-of-five squash match is stored game by game, so an 11-9 8-11 11-7 11-5 scoreline is kept in full rather than reduced to a single number."}},
                    {"@type": "Question", "name": "Is this different from squash tournament software?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Squash league software is built for recurring club play across a season, while squash tournament software is built for one-off events with draws, seeds and knockout rounds."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Squash League Software", item: "https://scorepal.co/squash-league-software/"}]} />

            {/* Hero */}
            <section className="relative overflow-hidden p-4">
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Squash League Software
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(38px,5.5vw,68px)] leading-none font-bold tracking-wide mb-5">
                                    Run club box leagues and ladders that keep every court busy
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    ScorePal helps squash clubs run box leagues and ladders — with game-by-game scores,
                                    court slots, promotion and relegation between rounds, and club rankings members can
                                    check for themselves.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Get started - it's free</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/squash-tournament-software/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Running a Tournament?
                                    </Link>
                                </div>
                            </div>

                            {/* Live box league card */}
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 lg:p-8 backdrop-blur-sm">
                                <h3 className="font-bebas text-white text-2xl font-bold tracking-wide mb-2">Live box league standings</h3>
                                <p className="font-inter text-white/70 text-sm mb-5">Members see where they sit and who they still owe a match, round by round.</p>
                                <div className="rounded-2xl bg-white/10 p-3">
                                    <div className="grid grid-cols-[28px_1fr_44px_44px] items-center gap-2 pb-2 border-b border-white/15 font-inter text-white/50 text-xs uppercase tracking-wide">
                                        <span>#</span><span>Player</span><span className="text-center">P</span><span className="text-right">Pts</span>
                                    </div>
                                    {[
                                        {pos: 1, name: "R. Kaur", played: "5", pts: "44"},
                                        {pos: 2, name: "T. Nowak", played: "5", pts: "39"},
                                        {pos: 3, name: "S. Iqbal", played: "4", pts: "31"},
                                        {pos: 4, name: "D. Clarke", played: "4", pts: "26"},
                                    ].map((row, i, arr) => (
                                        <div key={row.pos} className={`grid grid-cols-[28px_1fr_44px_44px] items-center gap-2 py-3 ${i < arr.length - 1 ? "border-b border-white/10" : ""}`}>
                                            <span className="font-inter text-white/60 text-sm">{row.pos}</span>
                                            <strong className="font-inter text-white text-sm">{row.name}</strong>
                                            <span className="font-inter text-white/70 text-sm text-center">{row.played}</span>
                                            <strong className="font-inter text-white text-sm text-right">{row.pts}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-16 lg:py-24 bg-white scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Everything you need to run a squash league
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Replace the noticeboard boxes, the paper result slips and the spreadsheet only one volunteer understands.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <div className="w-11 h-11 rounded-xl bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{f.code}</div>
                                <h3 className="font-inter text-[#1a1a2e] font-semibold text-lg mb-2">{f.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* League types */}
            <section id="league-types" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Support for every type of squash league
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Club squash runs on boxes and ladders. ScorePal handles both, and the seasons that string them together.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {LEAGUE_TYPES.map((f) => (
                            <Link key={f.name} to={f.to as any} className="group bg-white border border-[#e5e7eb] rounded-2xl p-5 hover:-translate-y-1 hover:border-[#082F8A]/25 transition">
                                <div className="font-inter font-bold text-[#1a1a2e] group-hover:text-[#082F8A]">{f.name}</div>
                                <div className="font-inter text-[#1a1a2e]/60 text-sm mt-1">{f.sub}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Club play + mock app */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                            Designed for how club squash actually runs
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 text-base leading-relaxed mb-6">
                            Box leagues live or die on whether members get their matches played. The software has to make
                            it obvious who owes whom a game, keep the court booking in view, and settle the table the
                            moment a result is in.
                        </p>
                        <div className="space-y-4">
                            {BENEFITS.map((b) => (
                                <div key={b.title} className="flex gap-3">
                                    <CheckBadgeIcon size={24} />
                                    <div>
                                        <strong className="font-inter text-[#1a1a2e]">{b.title}</strong>
                                        <p className="font-inter text-[#1a1a2e]/70 text-sm">{b.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mock app */}
                    <div className="rounded-3xl bg-linear-to-br from-[#eef4ff] to-white border border-[#e5e7eb] p-6 lg:p-8 shadow-sm">
                        <div className="bg-white border border-[#e5e7eb] rounded-2xl p-5">
                            {[
                                {l: "Winter Box League — Round 4", r: "Active", badge: "live"},
                                {l: "Your next match", r: "vs T. Nowak"},
                                {l: "Court", r: "Court 2 · Tuesday 19:20"},
                                {l: "Last result", r: "11-9 8-11 11-7 11-5"},
                                {l: "Box leader", r: "R. Kaur", badge: "gold"},
                            ].map((row, i, arr) => (
                                <div key={row.l} className={`flex items-center justify-between gap-3 py-3.5 font-inter text-sm ${i < arr.length - 1 ? "border-b border-[#eef0f4]" : ""}`}>
                                    <span className="text-[#1a1a2e]/70">{row.l}</span>
                                    {row.badge === "live"
                                        ? <span className="rounded-full bg-[#ecfdf3] text-[#12b76a] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                        : row.badge === "gold"
                                            ? <span className="rounded-full bg-[#fffbeb] text-[#b54708] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                            : <strong className="text-[#1a1a2e] text-right">{row.r}</strong>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Comparison */}
            <section id="comparison" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        ScorePal vs spreadsheets for squash leagues
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        A box league spreadsheet works until members start asking who they still owe a match, and every round has to be rebuilt by hand.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[680px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Need</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Spreadsheets</div>
                                <div className="p-5 text-[#082F8A]">ScorePal</div>
                            </div>
                            {COMPARE.map((row, i) => (
                                <div key={row.need} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] font-semibold">{row.need}</div>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/70">{row.manual}</div>
                                    <div className="p-5 font-medium">{row.scorepal}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Use cases */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Built for clubs, coaches and member competitions
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        From a winter box league to a year-round ladder, ScorePal keeps club squash organised across every standard of player.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {USE_CASES.map((u) => (
                            <div key={u.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{u.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{u.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA card → tournament software */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="rounded-3xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center">
                    <div>
                        <h2 className="font-bebas text-white uppercase text-[clamp(26px,3.5vw,40px)] leading-tight font-bold tracking-wide mb-4">
                            Running a club championship or open?
                        </h2>
                        <p className="font-inter text-white/80 text-base leading-relaxed mb-6">
                            This page is about recurring club leagues. If you are running a championship weekend with
                            draws, seeds, plate rounds and finals, see our squash tournament software page.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to={"/squash-tournament-software/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                                <span className="font-inter text-base font-medium">View Tournament Software</span>
                                <div className="bg-[#082F8A] rounded-full p-1">
                                    <ArrowUpRightIcon className="text-white" size={28} />
                                </div>
                            </Link>
                            <Link to={"/signup/player/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                Get started - it's free
                            </Link>
                        </div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                        <h3 className="font-inter text-white font-semibold mb-3">Popular tournament features</h3>
                        <ul className="list-disc pl-5 space-y-1.5 font-inter text-sm text-white/85">
                            <li>Knockout draws</li>
                            <li>Seeding</li>
                            <li>Plate & consolation rounds</li>
                            <li>Graded divisions</li>
                            <li>Finals day scheduling</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="container mx-auto px-6 lg:px-12 pb-8 lg:pb-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run your next squash league with ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Box leagues, ladders, court slots, game-by-game scores and club rankings — squash league software built for modern clubs.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Box Leagues", "Ladder Leagues", "Game Scores", "Court Slots", "Divisions", "Club Rankings"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Squash League Software FAQs"} faqs={FAQS} />
            <FooterSection />
        </div>
    );
}
